/**
 * Utilitários para manipulação de cookies com configurações de segurança
 * Otimizado para domínio .gs1pt.org com configurações GDPR compliant
 */

/**
 * Define um cookie com configurações de segurança para o domínio .gs1pt.org
 * @param name Nome do cookie
 * @param value Valor do cookie
 * @param days Dias de validade (padrão: 365)
 */
export function setRootCookie(name: string, value: string, days = 365): void {
  const maxAge = days * 24 * 60 * 60;
  
  // Requer HTTPS para "Secure"; usa Lax para não quebrar navegação básica
  // Codifica apenas o nome, não o valor (compatibilidade Complianz)
  document.cookie = [
    `${encodeURIComponent(name)}=${value}`,
    `Domain=.gs1pt.org`,
    `Path=/`,
    `Max-Age=${maxAge}`,
    `Secure`,
    `SameSite=Lax`
  ].join('; ');
}

/**
 * Obtém um cookie pelo nome
 * @param name Nome do cookie a ser recuperado
 * @returns Valor do cookie ou null se não existir
 */
export function getCookie(name: string): string | null {
  const encodedName = encodeURIComponent(name);
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    
    if (cookieName === encodedName) {
      return cookieValue || '';
    }
  }
  
  return null;
}

/**
 * Remove um cookie do domínio .gs1pt.org
 * @param name Nome do cookie a ser removido
 */
export function deleteRootCookie(name: string): void {
  document.cookie = [
    `${encodeURIComponent(name)}=`,
    `Domain=.gs1pt.org`,
    `Path=/`,
    `Max-Age=0`,
    `Secure`,
    `SameSite=Lax`
  ].join('; ');
}

/**
 * Verifica se um cookie existe
 * @param name Nome do cookie
 * @returns true se o cookie existir
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Define um cookie com opções avançadas
 * @param name Nome do cookie
 * @param value Valor do cookie
 * @param options Opções adicionais
 */
export function setAdvancedCookie(
  name: string, 
  value: string, 
  options: {
    days?: number;
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
  } = {}
): void {
  const {
    days = 365,
    domain = '.gs1pt.org',
    path = '/',
    secure = true,
    sameSite = 'Lax'
  } = options;

  const maxAge = days * 24 * 60 * 60;
  
  const cookieParts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Domain=${domain}`,
    `Path=${path}`,
    `Max-Age=${maxAge}`
  ];

  if (secure) {
    cookieParts.push('Secure');
  }

  if (sameSite) {
    cookieParts.push(`SameSite=${sameSite}`);
  }

  document.cookie = cookieParts.join('; ');
}

/**
 * Obtém todos os cookies do domínio atual
 * @returns Objeto com todos os cookies
 */
export function getAllCookies(): { [key: string]: string } {
  const cookies: { [key: string]: string } = {};
  
  if (!document.cookie) {
    return cookies;
  }

  document.cookie.split(';').forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });

  return cookies;
}

/**
 * Remove todos os cookies do domínio .gs1pt.org
 */
export function clearAllRootCookies(): void {
  const cookies = getAllCookies();
  
  Object.keys(cookies).forEach(cookieName => {
    deleteRootCookie(cookieName);
  });
}

/**
 * Define um cookie JSON (serializa objeto)
 * @param name Nome do cookie
 * @param value Objeto a ser armazenado
 * @param days Dias de validade
 */
export function setJsonCookie(name: string, value: any, days = 365): void {
  try {
    const jsonString = JSON.stringify(value);
    setRootCookie(name, jsonString, days);
  } catch (error) {
    console.error('Erro ao serializar cookie JSON:', error);
  }
}

/**
 * Obtém um cookie JSON (deserializa objeto)
 * @param name Nome do cookie
 * @returns Objeto ou null
 */
export function getJsonCookie<T = any>(name: string): T | null {
  try {
    const cookieValue = getCookie(name);
    return cookieValue ? JSON.parse(cookieValue) : null;
  } catch (error) {
    console.error('Erro ao deserializar cookie JSON:', error);
    return null;
  }
}

/**
 * Verifica se os cookies estão habilitados no navegador
 * @returns true se cookies estiverem habilitados
 */
export function areCookiesEnabled(): boolean {
  try {
    const testName = 'test_cookie_' + Date.now();
    setRootCookie(testName, 'test', 1);
    const enabled = hasCookie(testName);
    deleteRootCookie(testName);
    return enabled;
  } catch (error) {
    return false;
  }
}

/**
 * Obtém informações sobre um cookie (data de expiração, etc.)
 * @param name Nome do cookie
 * @returns Informações do cookie
 */
export function getCookieInfo(name: string): {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: string;
  expires?: Date;
} | null {
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [cookieName, ...attributes] = cookie.trim().split('=');
    
    if (cookieName === encodeURIComponent(name)) {
      const value = attributes.join('=');
      const info: any = {
        name: decodeURIComponent(cookieName),
        value: decodeURIComponent(value)
      };

      // Parse attributes
      for (let i = 1; i < attributes.length; i++) {
        const [attrName, attrValue] = attributes[i].split('=');
        const lowerAttrName = attrName?.toLowerCase();
        
        switch (lowerAttrName) {
          case 'domain':
            info.domain = attrValue;
            break;
          case 'path':
            info.path = attrValue;
            break;
          case 'secure':
            info.secure = true;
            break;
          case 'samesite':
            info.sameSite = attrValue;
            break;
          case 'expires':
            info.expires = new Date(attrValue);
            break;
        }
      }
      
      return info;
    }
  }
  
  return null;
}

/**
 * Exporta todas as funções para uso fácil
 */
export const CookieUtils = {
  set: setRootCookie,
  get: getCookie,
  delete: deleteRootCookie,
  has: hasCookie,
  setAdvanced: setAdvancedCookie,
  getAll: getAllCookies,
  clearAll: clearAllRootCookies,
  setJson: setJsonCookie,
  getJson: getJsonCookie,
  isEnabled: areCookiesEnabled,
  getInfo: getCookieInfo
};

/**
 * Constantes para nomes de cookies comuns
 */
export const CookieNames = {
  CONSENT: 'cookie_consent',
  ANALYTICS: 'ga_consent',
  MARKETING: 'marketing_consent',
  PREFERENCES: 'user_preferences',
  SESSION: 'session_id',
  LANGUAGE: 'user_lang',
  THEME: 'theme_preference'
} as const;

/**
 * Tipos para TypeScript
 */
export type CookieSameSite = 'Strict' | 'Lax' | 'None';
export type CookieOptions = {
  days?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: CookieSameSite;
};